import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SUPPORTED_CURRENCIES, formatMinorAmount, type ReceivablesOutput, type InvoiceBrandingProfileOutput } from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { ClientSearchSelect } from './ClientSearchSelect.js';
import { DateInput } from './DateInput.js';
import { FirmReceivablesPanel } from './FirmReceivablesPanel.js';
import { fmtDate } from '../lib/date.js';

const field = 'w-full bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent';

function money(value: number, currency: string) {
  return `${formatMinorAmount(value, true)} ${currency}`;
}

export function ReceivablesPanel() {
  const [clientId, setClientId] = useState('');
  const [currency, setCurrency] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [view, setView] = useState<'balances' | 'transactions' | 'statement'>('balances');
  const [brandingProfileId, setBrandingProfileId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const profiles = useQuery({
    queryKey: ['invoice-branding-profiles'],
    queryFn: async () => {
      const res = await fetch('/v1/invoice-branding/profiles', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: InvoiceBrandingProfileOutput[] } };
      return body.data.items;
    },
  });
  const activeProfiles = (profiles.data ?? []).filter((profile) => profile.active);
  useEffect(() => {
    if (!brandingProfileId) {
      const fallback = activeProfiles.find((profile) => profile.isDefault) ?? activeProfiles[0];
      if (fallback) setBrandingProfileId(fallback.id);
    }
  }, [brandingProfileId, profiles.data]);
  const ledger = useQuery({
    queryKey: ['receivables', clientId, currency, from, to],
    enabled: !!clientId,
    queryFn: async () => {
      const params = new URLSearchParams({ clientId });
      if (currency) params.set('currency', currency);
      if (from) params.set('from', `${from}T00:00:00.000Z`);
      if (to) params.set('to', `${to}T00:00:00.000Z`);
      const res = await fetch(`/v1/invoices/receivables?${params}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: ReceivablesOutput };
      return body.data;
    },
  });

  function exportCsv() {
    if (!ledger.data) return;
    const rows: unknown[][] = [['Currency', 'Date', 'Type', 'Reference', 'Details', 'Debit', 'Credit', 'Balance', 'Status', 'Due date']];
    for (const group of ledger.data.groups) {
      if (group.openingBalance) rows.push([group.currency, from, 'OPENING', '', 'Opening balance', '', '', group.openingBalance / 100, '', '']);
      for (const entry of group.entries) rows.push([group.currency, entry.date.slice(0, 10), entry.type, entry.reference, entry.details ?? '', entry.debit / 100, entry.credit / 100, entry.balance / 100, entry.status, entry.dueDate.slice(0, 10)]);
      rows.push([group.currency, to, 'CLOSING', '', 'Closing balance', '', '', group.closingBalance / 100, '', '']);
    }
    const csv = rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = `receivables-${ledger.data.client.clientCode}.csv`; anchor.click();
    URL.revokeObjectURL(url);
  }

  async function downloadPdf() {
    if (!ledger.data || !brandingProfileId) return;
    setDownloading(true); setPdfError(null);
    try {
      const params = new URLSearchParams({ clientId, brandingProfileId });
      if (currency) params.set('currency', currency);
      if (from) params.set('from', `${from}T00:00:00.000Z`);
      if (to) params.set('to', `${to}T00:00:00.000Z`);
      const res = await fetch(`/v1/invoices/receivables/pdf?${params}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const url = URL.createObjectURL(await res.blob());
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = `receivables-${ledger.data.client.clientCode}.pdf`; anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : 'PDF generation failed');
    } finally { setDownloading(false); }
  }

  if (view !== 'statement') {
    return (
      <div className="space-y-4">
        <WorkspaceTabs view={view} onChange={setView} />
        <FirmReceivablesPanel mode={view} onSelectClient={(id) => { setClientId(id); setView('statement'); }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <WorkspaceTabs view={view} onChange={setView} />
      <div className="bg-bg-panel border border-border rounded-xl p-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
        <ClientSearchSelect value={clientId} onChange={(id) => setClientId(id)} placeholder="Select client" className={field} wrapperClassName="w-full" />
        <select aria-label="Ledger currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className={field}>
          <option value="">All currencies</option>{SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <DateInput aria-label="Ledger from date" value={from} onChange={(e) => setFrom(e.target.value)} className={field} />
        <DateInput aria-label="Ledger to date" value={to} onChange={(e) => setTo(e.target.value)} className={field} />
      </div>

      {!clientId && <div className="bg-bg-panel border border-border rounded-xl p-10 text-center text-sm text-muted">Select a client to view their receivable ledger.</div>}
      {clientId && ledger.isLoading && <div className="bg-bg-panel border border-border rounded-xl p-10 text-center text-sm text-muted">Building the client statement…</div>}
      {ledger.error && <div className="bg-bg-panel border border-rose-800 rounded-xl p-4 text-sm text-rose-300">{(ledger.error as Error).message}</div>}

      {ledger.data && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-white">{ledger.data.client.company ?? ledger.data.client.name}</h2>
              <p className="text-xs text-muted">{ledger.data.client.name} · {ledger.data.client.clientCode}{from || to ? ` · ${from || 'Beginning'} to ${to || 'Today'}` : ' · All activity'}</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <select aria-label="Statement branding" value={brandingProfileId} onChange={(e) => setBrandingProfileId(e.target.value)} className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white max-w-52">
                {activeProfiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
              </select>
              <button type="button" onClick={exportCsv} className="border border-border rounded-lg px-3 py-1.5 text-sm text-white hover:bg-bg-hover">Export CSV</button>
              <button type="button" disabled={downloading || !brandingProfileId} onClick={() => void downloadPdf()} className="bg-accent text-accent-fg rounded-lg px-3 py-1.5 text-sm disabled:opacity-50">{downloading ? 'Generating PDF…' : 'Download PDF statement'}</button>
            </div>
          </div>
          {pdfError && <p className="text-sm text-rose-300">{pdfError}</p>}
          {ledger.data.groups.length === 0 && <div className="bg-bg-panel border border-border rounded-xl p-10 text-center text-sm text-muted">No issued invoices or payments match these filters.</div>}
          {ledger.data.groups.map((group) => (
            <section key={group.currency} className="space-y-3">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SummaryCard label={`Invoiced · ${group.currency}`} value={formatMinorAmount(group.invoiced, true)} />
                <SummaryCard label={`Received · ${group.currency}`} value={formatMinorAmount(group.received, true)} />
                <SummaryCard label={`Outstanding · ${group.currency}`} value={formatMinorAmount(group.outstanding, true)} />
                <SummaryCard label={`Overdue · ${group.currency}`} value={formatMinorAmount(group.overdue, true)} tone="rose" />
              </div>
              <div className="bg-bg-panel border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-sm min-w-[1000px]">
                  <thead><tr className="bg-bg-card border-b border-border text-left text-xs text-muted">
                    <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium">Reference</th><th className="px-4 py-3 font-medium">Details</th><th className="px-4 py-3 font-medium text-right">Debit</th><th className="px-4 py-3 font-medium text-right">Credit</th><th className="px-4 py-3 font-medium text-right">Balance</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Due</th>
                  </tr></thead>
                  <tbody className="divide-y divide-border/50">
                    {group.openingBalance > 0 && <tr className="bg-bg-hover/30"><td className="px-4 py-3 text-muted">{from ? fmtDate(from) : 'Opening'}</td><td className="px-4 py-3 text-muted">OPENING</td><td className="px-4 py-3 text-muted">—</td><td className="px-4 py-3 text-muted">Opening balance</td><td className="px-4 py-3" /><td className="px-4 py-3" /><td className="px-4 py-3 text-right text-white">{money(group.openingBalance, group.currency)}</td><td /><td /></tr>}
                    {group.entries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-bg-hover/30">
                        <td className="px-4 py-3 text-muted">{fmtDate(entry.date)}</td>
                        <td className={`px-4 py-3 text-xs ${entry.type === 'PAYMENT' ? 'text-emerald-300' : entry.type === 'REVERSAL' ? 'text-amber-300' : 'text-white'}`}>{entry.type}</td>
                        <td className="px-4 py-3"><Link to={`/invoices/${entry.invoiceId}`} className="text-accent hover:underline">{entry.reference}</Link></td>
                        <td className="px-4 py-3 text-muted max-w-72 truncate" title={entry.details ?? undefined}>{entry.details ?? '—'}</td>
                        <td className="px-4 py-3 text-right text-white">{entry.debit ? money(entry.debit, group.currency) : '—'}</td>
                        <td className="px-4 py-3 text-right text-emerald-300">{entry.credit ? money(entry.credit, group.currency) : '—'}</td>
                        <td className="px-4 py-3 text-right text-white">{money(entry.balance, group.currency)}</td>
                        <td className="px-4 py-3 text-xs text-muted">{entry.status.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-muted">{fmtDate(entry.dueDate)}</td>
                      </tr>
                    ))}
                    <tr className="bg-bg-card font-medium"><td className="px-4 py-3 text-muted" colSpan={6}>Closing balance · {group.currency}</td><td className="px-4 py-3 text-right text-white">{money(group.closingBalance, group.currency)}</td><td colSpan={2} /></tr>
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

function WorkspaceTabs({ view, onChange }: { view: 'balances' | 'transactions' | 'statement'; onChange: (view: 'balances' | 'transactions' | 'statement') => void }) {
  const tabs = [
    ['balances', 'Client balances'], ['transactions', 'All transactions'], ['statement', 'Client statement'],
  ] as const;
  return <div className="flex flex-wrap rounded-lg border border-border overflow-hidden w-fit">
    {tabs.map(([key, label]) => <button key={key} type="button" onClick={() => onChange(key)} className={`px-3 py-1.5 text-sm ${view === key ? 'bg-bg-hover text-white' : 'text-muted hover:text-white'}`}>{label}</button>)}
  </div>;
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone?: 'rose' }) {
  return <div className="bg-bg-panel border border-border rounded-xl p-4"><div className="text-[11px] text-muted">{label}</div><div className={`mt-1 text-lg font-semibold ${tone === 'rose' ? 'text-rose-300' : 'text-white'}`}>{value}</div></div>;
}
